/**
 * <SamWelcome> — l'accueil de la conversation avec Sam (molecule).
 *
 * Ce que la feuille montre tant que rien ne s'est dit : Sam se présente, pose sa question, et
 * propose quatre phrases toutes faites, à la première personne, que l'élève n'a plus qu'à
 * choisir. L'obstacle numéro un quand on bloque, c'est de formuler sa question.
 *
 * C'est le seul endroit où Sam est grand (la taille 72 du livre) : ici son identité est
 * l'information. Dès que la conversation démarre, l'accueil laisse la place au fil. Ce n'est pas
 * un écran vide (qui, lui, n'a jamais de champion) : Sam y agit, il ouvre la conversation.
 *
 * Les cartes de démarrage sont les mêmes actions que les suggestions rapides du fil, dites
 * autrement (« Indice » devient « Donne-moi un indice pour démarrer ») : c'est l'écran qui
 * tient la liste. Sans carte (Sam est fermé pour la journée), il ne pose pas sa question non
 * plus : il se présente, et le composeur fermé dit quand il revient.
 *
 * Composes DS pieces only — `<ChampionAvatar>`, `<Title>`, `<Text>`, `<Card>`, `<Icon>`.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { ChampionAvatar } from "../../atoms/Avatar/ChampionAvatar"
import { Stack } from "../../atoms/containers/Stack"
import { Icon, type IconName } from "../../atoms/icons/Icon"
import { Text } from "../../atoms/typography/Text"
import { Title } from "../../atoms/typography/Title"
import { frenchSpacing } from "../../utils/frenchSpacing"
import { Card } from "../Card"

/** Une carte de démarrage. À remonter dans `ports/primary/ui/types` au câblage. */
export type UISamStarter = {
    key: string
    /** La phrase entière, à la première personne. */
    label: string
    iconName: IconName
}

export type SamWelcomeProps = {
    /** Les cartes de démarrage. Vide : Sam se présente, sans question ni carte. */
    starters: UISamStarter[]
    onSelect: (key: string) => void
    testID?: string
}

const TEXTS = {
    title: "Salut, moi c'est Sam.",
    question: "Sur quoi tu bloques ?",
}

/** La plus grande taille du livre : le seul moment où Sam est grand. */
const WELCOME_HEAD_SIZE = 72
/** La grille ne s'étire pas sur toute une colonne de grand écran. */
const WELCOME_MAX_WIDTH = 420

export function SamWelcome(props: SamWelcomeProps) {
    const { starters, onSelect, testID } = props
    const hasStarters = starters.length > 0

    return (
        <View style={styles.root} testID={testID}>
            <Stack gap="2" align="center" style={styles.column}>
                <View style={styles.head}>
                    <ChampionAvatar name="sam" size={WELCOME_HEAD_SIZE} />
                </View>
                <Title center>{TEXTS.title}</Title>

                {hasStarters && (
                    <Text color="text-muted" size="text-lg" center>
                        {frenchSpacing(TEXTS.question)}
                    </Text>
                )}
                {hasStarters && (
                    <View style={styles.grid}>
                        {starters.map((starter) => (
                            <Card
                                key={starter.key}
                                pressable
                                onPress={() => onSelect(starter.key)}
                                p="3"
                                style={styles.starter}
                            >
                                <Stack gap="1">
                                    <Icon name={starter.iconName} size={20} color="role-strong" />
                                    <Text size="text-md" weight="semibold">
                                        {starter.label}
                                    </Text>
                                </Stack>
                            </Card>
                        ))}
                    </View>
                )}
            </Stack>
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    /** Au milieu de la place que le fil occupera. */
    root: {
        flexGrow: 1,
        justifyContent: "center",
        alignItems: "center",
    },
    column: {
        width: "100%",
        maxWidth: WELCOME_MAX_WIDTH,
    },
    /** Le nœud pend sous le disque : il lui faut sa place avant le titre. */
    head: {
        paddingBottom: theme.spacing["2"],
    },
    /** Deux cartes par ligne. */
    grid: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: theme.spacing["2"],
        marginTop: theme.spacing["3"],
        alignSelf: "stretch",
    },
    starter: {
        flexBasis: "45%",
        flexGrow: 1,
    },
}))
